// Broadcast Messages — SUPER_ADMIN / MANAGEMENT / SALES_COORDINATOR send one
// message to many users at once: everyone, by role / zone / sub-zone, or
// hand-picked people. Each recipient gets a bell notification plus a popup that
// stays until they tap "OK, I have read it"; the report shows who has and
// hasn't read it. Backend: routes_broadcasts.py
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Megaphone, Send, Search, RefreshCw, Paperclip, X, Eye, Download, Check,
  ChevronsUpDown, AlertTriangle, Users, Filter, UserCheck, Ban, BarChart3, FileText,
  FileSpreadsheet, Image as ImageIcon, CalendarClock,
} from "lucide-react";
import { api, formatApiError, API_BASE, getToken } from "@/lib/api";
import { uploadInChunks, cancelUpload, shrinkImage, extOf } from "@/lib/chunkedUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";

const TITLE_MAX = 150;
const BODY_MAX = 4000;
const ACCEPT = ".pdf,.xlsx,.xls,.docx,.doc,.jpg,.jpeg,.png,.webp";
const ALLOWED_EXT = new Set(ACCEPT.split(","));
const MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024;   // total of all files in one message
const PAGE_SIZE = 20;
const REPORT_PAGE_SIZE = 50;

// Used only if GET /broadcasts/audience-options returns no roles.
const ROLE_FALLBACK = [
  ["SUPER_ADMIN", "Super Admin"], ["MANAGEMENT", "Management"], ["SALES_HEAD", "Sales Head"],
  ["MIS", "Backend MIS"], ["ACCOUNTS", "Accounts"], ["SALES_COORDINATOR", "Sales Co-ordinator"],
  ["ZSM", "Zonal Sales Manager"], ["RSM", "Regional Sales Manager"], ["ASM", "Area Sales Manager"],
  ["SE", "Sales Executive"], ["DISTRIBUTOR", "Distributor"],
].map(([code, name]) => ({ code, name }));

const MODES = [
  { key: "all", label: "All users", hint: "Everyone who can log in", icon: Users },
  { key: "filter", label: "By role / zone", hint: "Pick roles, zones or sub-zones", icon: Filter },
  { key: "users", label: "Pick users", hint: "Choose people by name", icon: UserCheck },
];

const STATUS_META = {
  active:    { label: "Active",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  expired:   { label: "Expired",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  withdrawn: { label: "Withdrawn", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
};

const REPORT_TABS = [["all", "All"], ["read", "Read"], ["unread", "Not read"]];

function fmtSize(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

// Backend stores YYYY-MM-DD; users read DD-MM-YYYY.
function fmtYmd(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-");
  return d && m && y ? `${d}-${m}-${y}` : ymd;
}

// Expiry is judged on the India date, same as the server.
function todayIST() {
  return new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
}

function statusOf(b) {
  if (b?.is_active === false) return "withdrawn";
  if (b?.expires_on && b.expires_on < todayIST()) return "expired";
  return "active";
}

function isViewable(att) {
  const ct = att?.content_type || "";
  return ct.startsWith("image/") || ct === "application/pdf" || /\.(pdf|jpe?g|png|webp)$/i.test(att?.name || "");
}

function fileIcon(name, type = "") {
  const ext = extOf(name);
  if (type.startsWith("image/") || [".jpg", ".jpeg", ".png", ".webp"].includes(ext)) return ImageIcon;
  if ([".xlsx", ".xls"].includes(ext)) return FileSpreadsheet;
  return FileText;
}

// ---- authed file fetches (a plain <a href> to the API has no token) ----------
async function authedFetch(path) {
  const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function filenameFrom(res, fallback) {
  const cd = res.headers.get("Content-Disposition") || "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) { try { return decodeURIComponent(star[1]); } catch { /* fall through */ } }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

// PDFs and photos open in a new tab, other files download. The tab is opened
// before the fetch so pop-up blockers count it as part of the tap.
async function openAttachment(att) {
  const win = isViewable(att) ? window.open("", "_blank") : null;
  try {
    const blob = await (await authedFetch(`/broadcasts/attachments/${encodeURIComponent(att.id)}?inline=1`)).blob();
    if (win) {
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      // free the file's memory once the new tab has had time to load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else saveBlob(blob, att.name);
  } catch {
    if (win) win.close();
    toast.error("Could not open this file. Please try again.");
  }
}

async function downloadAttachment(att) {
  try {
    const blob = await (await authedFetch(`/broadcasts/attachments/${encodeURIComponent(att.id)}?inline=0`)).blob();
    saveBlob(blob, att.name);
  } catch {
    toast.error("Could not download this file. Please try again.");
  }
}

// ---- small pieces -------------------------------------------------------------
function PriorityBadge({ priority }) {
  return priority === "urgent" ? (
    <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 whitespace-nowrap">
      <AlertTriangle className="h-3 w-3" /> Urgent
    </span>
  ) : (
    <span className="inline-flex items-center rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-600 whitespace-nowrap">
      Normal
    </span>
  );
}

function StatusBadge({ b }) {
  const meta = STATUS_META[statusOf(b)];
  return (
    <span className={"inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap " + meta.cls}>
      {meta.label}
    </span>
  );
}

function ReadBar({ read = 0, total = 0 }) {
  const pct = total ? Math.round((read * 100) / total) : 0;
  return (
    <div className="min-w-[120px]">
      <div className="text-xs text-neutral-700">
        <b>{read}</b> / {total} read <span className="text-neutral-400">({pct}%)</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AttachmentChip({ att }) {
  const Icon = fileIcon(att.name, att.content_type || "");
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs max-w-full"
         data-testid={`bc-att-${att.id}`}>
      <Icon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
      <button type="button" onClick={() => openAttachment(att)}
              className="truncate min-w-0 text-left text-neutral-800 hover:text-[#6D2C91] hover:underline">
        {att.name}
      </button>
      <span className="text-neutral-400 shrink-0">{fmtSize(att.size)}</span>
      {isViewable(att) && (
        <button type="button" onClick={() => openAttachment(att)} aria-label={`View ${att.name}`}
                className="p-0.5 text-[#6D2C91] shrink-0" data-testid={`bc-att-view-${att.id}`}>
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      <button type="button" onClick={() => downloadAttachment(att)} aria-label={`Download ${att.name}`}
              className="p-0.5 text-[#6D2C91] shrink-0" data-testid={`bc-att-download-${att.id}`}>
        <Download className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Type-to-search multi-select; picked items show as removable chips below.
// options: [{ id, label, hint?, search? }]
function MultiPick({ options, value, onChange, placeholder, emptyMessage = "No match.", disabled, testid }) {
  const [open, setOpen] = useState(false);
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const picked = new Set(value);
  const toggle = (id) => onChange(picked.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  const chipText = (o) => (o.hint ? `${o.label} · ${o.hint}` : o.label);

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
                  className="w-full justify-between font-normal" data-testid={testid}>
            <span className="truncate text-left text-neutral-500">
              {value.length ? `${value.length} selected — add more…` : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[260px] max-w-[calc(100vw-2rem)] p-0" align="start">
          <Command
            filter={(itemValue, search) => {
              const o = byId.get(itemValue);
              if (!o) return 0;
              return (o.search || `${o.label} ${o.hint || ""}`).toLowerCase().includes(String(search).toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Type to search…" data-testid={testid ? `${testid}-search` : undefined} />
            <CommandList className="max-h-64">
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem key={o.id} value={o.id} onSelect={() => toggle(o.id)}
                               data-testid={testid ? `${testid}-opt-${o.id}` : undefined}>
                    <Check className={"mr-2 h-4 w-4 shrink-0 " + (picked.has(o.id) ? "opacity-100 text-[#6D2C91]" : "opacity-0")} />
                    <span className="flex-1 min-w-0 whitespace-normal leading-snug">{o.label}</span>
                    {o.hint && <span className="ml-2 shrink-0 text-[10px] text-neutral-400">{o.hint}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" data-testid={testid ? `${testid}-chips` : undefined}>
          {value.map((id) => {
            const o = byId.get(id);
            const text = o ? chipText(o) : "Unknown";
            return (
              <span key={id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#6D2C91]/10 py-0.5 pl-2.5 pr-1 text-xs text-[#6D2C91]">
                <span className="truncate">{text}</span>
                <button type="button" disabled={disabled} aria-label={`Remove ${text}`}
                        onClick={() => onChange(value.filter((v) => v !== id))}
                        className="rounded-full p-0.5 hover:bg-[#6D2C91]/15 shrink-0">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          {value.length > 1 && (
            <button type="button" disabled={disabled} onClick={() => onChange([])}
                    className="text-[11px] text-neutral-500 underline hover:text-neutral-800 ml-1">
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Photos are shrunk as soon as they are picked, so the sizes and the 10 MB
// limit shown here match what is actually uploaded.
function FilePicker({ files, onChange, disabled }) {
  const inputRef = useRef(null);
  const [preparing, setPreparing] = useState(false);
  const totalBytes = files.reduce((n, f) => n + f.size, 0);

  const pick = async (picked) => {
    setPreparing(true);
    try {
      const next = [...files];
      let total = totalBytes;
      for (const raw of picked) {
        if (next.length >= MAX_FILES) { toast.error(`You can attach at most ${MAX_FILES} files.`); break; }
        if (!ALLOWED_EXT.has(extOf(raw.name))) { toast.error(`${raw.name}: only PDF, Excel, Word or photos (JPG, PNG, WEBP) can be attached.`); continue; }
        if (raw.size === 0) { toast.error(`${raw.name} is empty.`); continue; }
        const f = await shrinkImage(raw);
        if (next.some((x) => x.name === f.name && x.size === f.size)) { toast.error(`${raw.name} is already attached.`); continue; }
        if (total + f.size > MAX_BYTES) {
          toast.error(`${raw.name}: attachments can be at most 10 MB in total (you have ${fmtSize(total)} already).`);
          continue;
        }
        total += f.size;
        next.push(f);
      }
      onChange(next);
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          e.target.value = "";
          if (picked.length) pick(picked);
        }}
        data-testid="bc-files-input"
      />
      <Button type="button" variant="outline" size="sm" disabled={disabled || preparing || files.length >= MAX_FILES}
              onClick={() => inputRef.current?.click()} data-testid="bc-files-btn">
        {preparing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Paperclip className="h-4 w-4 mr-1.5" />}
        {preparing ? "Preparing…" : "Attach files"}
        <span className="ml-1.5 text-[10px] text-neutral-400">({files.length}/{MAX_FILES} · {fmtSize(totalBytes)} of 10 MB)</span>
      </Button>
      <div className="text-[11px] text-neutral-400">PDF, photos, Excel or Word. Big photos are made smaller automatically.</div>
      {files.length > 0 && (
        <div className="space-y-1" data-testid="bc-files-list">
          {files.map((f, i) => {
            const Icon = fileIcon(f.name, f.type);
            return (
              <div key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs">
                <Icon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                <span className="truncate flex-1 min-w-0">{f.name}</span>
                <span className="text-neutral-400 shrink-0">{fmtSize(f.size)}</span>
                <button type="button" aria-label={`Remove ${f.name}`} disabled={disabled}
                        onClick={() => onChange(files.filter((_, j) => j !== i))}
                        data-testid={`bc-files-remove-${i}`}>
                  <X className="h-3.5 w-3.5 text-rose-500" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- composer -----------------------------------------------------------------
function Composer({ roleOptions, zoneOptions, subZoneOptions, userOptions, usersLoading, onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [expiresOn, setExpiresOn] = useState("");
  const [mode, setMode] = useState("all");
  const [roleCodes, setRoleCodes] = useState([]);
  const [zoneIds, setZoneIds] = useState([]);
  const [subZoneIds, setSubZoneIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState({ status: "idle" }); // idle | loading | ok | error
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const previewSeq = useRef(0);
  // File -> upload_id, so a retry after one file failed doesn't re-upload the rest.
  const uploaded = useRef(new WeakMap());

  const audience = useMemo(() => ({
    mode,
    role_codes: mode === "filter" ? roleCodes : [],
    zone_ids: mode === "filter" ? zoneIds : [],
    sub_zone_ids: mode === "filter" ? subZoneIds : [],
    user_ids: mode === "users" ? userIds : [],
  }), [mode, roleCodes, zoneIds, subZoneIds, userIds]);

  const audienceReady = mode === "all"
    || (mode === "filter" && (roleCodes.length + zoneIds.length + subZoneIds.length) > 0)
    || (mode === "users" && userIds.length > 0);

  // Live "Will be sent to N users" — debounced so ticking chips doesn't spam
  // (each preview is a POST, so it also lands in the audit log).
  useEffect(() => {
    const seq = ++previewSeq.current;
    if (!audienceReady) { setPreview({ status: "idle" }); return undefined; }
    setPreview((p) => ({ ...p, status: "loading" }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/broadcasts/audience-preview", { audience });
        if (seq === previewSeq.current) setPreview({ status: "ok", count: data.count || 0, sample: data.sample || [] });
      } catch (e) {
        if (seq === previewSeq.current) setPreview({ status: "error", error: formatApiError(e) });
      }
    }, 800);
    return () => clearTimeout(t);
  }, [audience, audienceReady]);

  // A removed file that was already uploaded (a failed send) is dropped on the
  // server too, so it doesn't count against the sender's unsent-upload limit.
  const changeFiles = (next) => {
    for (const f of files) {
      const id = !next.includes(f) && uploaded.current.get(f);
      if (id) { cancelUpload(id); uploaded.current.delete(f); }
    }
    setFiles(next);
  };

  const reset = () => {
    setTitle(""); setBody(""); setPriority("normal"); setExpiresOn("");
    setMode("all"); setRoleCodes([]); setZoneIds([]); setSubZoneIds([]); setUserIds([]);
    setFiles([]); setProgress(null);
    uploaded.current = new WeakMap();
  };

  const openConfirm = () => {
    if (!title.trim()) { toast.error("Please enter a title."); return; }
    if (!body.trim()) { toast.error("Please write the message."); return; }
    if (expiresOn && expiresOn < todayIST()) { toast.error("The expiry date can't be in the past."); return; }
    if (!audienceReady) {
      toast.error(mode === "users" ? "Pick at least one user." : "Pick at least one role, zone or sub-zone.");
      return;
    }
    if (preview.status === "loading") { toast.error("Still counting the users — please try again in a moment."); return; }
    if (preview.status === "error") { toast.error(preview.error); return; }
    if (preview.status === "ok" && preview.count === 0) {
      toast.error("No users match this audience. Please change who it goes to.");
      return;
    }
    setConfirmOpen(true);
  };

  const doSend = async () => {
    setSending(true);
    let stage = "upload";
    const uploadIds = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        let id = uploaded.current.get(f);
        if (!id) {
          setProgress({ index: i, count: files.length, name: f.name, percent: 0 });
          try {
            id = await uploadInChunks(f, {
              onProgress: ({ percent }) => setProgress({ index: i, count: files.length, name: f.name, percent }),
            });
          } catch (e) {
            throw new Error(`“${f.name}” could not be uploaded. ${formatApiError(e)}`);
          }
          uploaded.current.set(f, id);
        }
        uploadIds.push(id);
      }
      stage = "send";
      setProgress({ sending: true });
      const { data } = await api.post("/broadcasts", {
        title: title.trim(),
        body: body.trim(),
        priority,
        expires_on: expiresOn || null,
        audience,
        upload_ids: uploadIds,
      }, { timeout: 180000 });
      const n = data.recipient_count ?? preview.count ?? 0;
      toast.success(`Message sent to ${n} user${n === 1 ? "" : "s"}.`);
      reset();
      onSent(data);
    } catch (e) {
      toast.error(formatApiError(e));
      if (stage === "send") {
        // The server keeps uploads until a message is created, so a retry can
        // reuse them — unless it rejected them (expired / incomplete), then
        // upload again (dropping the old ones on the server). Refresh the list
        // in case a timed-out send went through.
        if (e?.response?.status === 422) {
          uploadIds.forEach((id) => cancelUpload(id));
          uploaded.current = new WeakMap();
        }
        onSent(null);
      }
      setProgress(null);
    } finally {
      setSending(false);
    }
  };

  const sampleText = () => {
    const sample = preview.sample || [];
    if (!sample.length) return "";
    const names = sample.slice(0, 8).map((u) => `${u.name}${u.role_code ? ` (${u.role_code})` : ""}`).join(", ");
    const more = (preview.count || 0) - Math.min(sample.length, 8);
    return more > 0 ? `${names} and ${more} more` : names;
  };

  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="bc-compose">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-[#6D2C91]" />
          <h2 className="text-sm font-bold text-neutral-900">New message</h2>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {/* ---- the message ---- */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input value={title} maxLength={TITLE_MAX} disabled={sending}
                     onChange={(e) => setTitle(e.target.value)}
                     placeholder="e.g. New scheme from 1st November" data-testid="bc-title" />
              <div className="text-[10px] text-neutral-400 text-right">{title.length}/{TITLE_MAX}</div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message</Label>
              <Textarea value={body} maxLength={BODY_MAX} rows={6} disabled={sending}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Write the message everyone should read"
                        data-testid="bc-body" />
              <div className={"text-[10px] text-right " + (body.length > BODY_MAX - 200 ? "text-amber-600" : "text-neutral-400")}>
                {body.length}/{BODY_MAX}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Priority</Label>
                <div className="flex rounded-xl border border-neutral-200 bg-neutral-50 p-0.5" role="radiogroup" aria-label="Priority">
                  <button type="button" role="radio" aria-checked={priority === "normal"} disabled={sending}
                          onClick={() => setPriority("normal")} data-testid="bc-priority-normal"
                          className={"flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors " +
                            (priority === "normal" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-800")}>
                    Normal
                  </button>
                  <button type="button" role="radio" aria-checked={priority === "urgent"} disabled={sending}
                          onClick={() => setPriority("urgent")} data-testid="bc-priority-urgent"
                          className={"flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors inline-flex items-center justify-center gap-1 " +
                            (priority === "urgent" ? "bg-red-600 text-white shadow-sm" : "text-neutral-500 hover:text-red-700")}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Urgent
                  </button>
                </div>
                <div className="text-[11px] text-neutral-400">
                  {priority === "urgent" ? "Shown in red and before other messages." : "Shown as a normal message."}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Expiry date (optional)</Label>
                <div className="flex items-center gap-1">
                  <Input type="date" value={expiresOn} min={todayIST()} disabled={sending}
                         onChange={(e) => setExpiresOn(e.target.value)} className="h-9" data-testid="bc-expires" />
                  {expiresOn && (
                    <button type="button" onClick={() => setExpiresOn("")} disabled={sending}
                            aria-label="Remove expiry date" className="p-1 text-neutral-400 hover:text-neutral-700">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="text-[11px] text-neutral-400">
                  {expiresOn ? `No popup after ${fmtYmd(expiresOn)}.` : "Leave empty to keep the popup until it is read."}
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Attachments (optional)</Label>
              <FilePicker files={files} onChange={changeFiles} disabled={sending} />
            </div>
          </div>

          {/* ---- who gets it ---- */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Send to</Label>
              <div role="radiogroup" aria-label="Who should get this message"
                   className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                {MODES.map((m) => {
                  const on = mode === m.key;
                  return (
                    <button key={m.key} type="button" role="radio" aria-checked={on} disabled={sending}
                            onClick={() => setMode(m.key)} data-testid={`bc-mode-${m.key}`}
                            className={"flex items-start gap-2 rounded-xl border p-2.5 text-left transition-colors " +
                              (on ? "border-[#6D2C91] bg-[#6D2C91]/5 ring-1 ring-[#6D2C91]/30" : "border-neutral-200 bg-white hover:bg-neutral-50")}>
                      <m.icon className={"h-4 w-4 mt-0.5 shrink-0 " + (on ? "text-[#6D2C91]" : "text-neutral-400")} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-neutral-900">{m.label}</span>
                        <span className="block text-[11px] text-neutral-500">{m.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {mode === "filter" && (
              <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3" data-testid="bc-filter-box">
                <div className="space-y-1">
                  <Label className="text-xs">Roles</Label>
                  <MultiPick options={roleOptions} value={roleCodes} onChange={setRoleCodes} disabled={sending}
                             placeholder="Any role" testid="bc-roles" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Zones</Label>
                  <MultiPick options={zoneOptions} value={zoneIds} onChange={setZoneIds} disabled={sending}
                             placeholder="Any zone" emptyMessage="No zone found." testid="bc-zones" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Sub-zones</Label>
                  <MultiPick options={subZoneOptions} value={subZoneIds} onChange={setSubZoneIds} disabled={sending}
                             placeholder="Any sub-zone" emptyMessage="No sub-zone found." testid="bc-subzones" />
                </div>
                <div className="text-[11px] text-neutral-500">
                  Leave a box empty to not filter by it. If you fill more than one box, a person must match all of them
                  (e.g. Role SE + Zone Gujarat = only the SEs of Gujarat).
                </div>
              </div>
            )}

            {mode === "users" && (
              <div className="space-y-1 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3" data-testid="bc-users-box">
                <Label className="text-xs">People</Label>
                <MultiPick options={userOptions} value={userIds} onChange={setUserIds} disabled={sending || usersLoading}
                           placeholder={usersLoading ? "Loading users…" : "Search by name, phone or role"}
                           emptyMessage="No user found." testid="bc-users" />
              </div>
            )}

            <div className="rounded-xl border border-neutral-200 bg-white p-3 text-sm" data-testid="bc-preview">
              {preview.status === "idle" && (
                <span className="text-neutral-500">
                  {mode === "users" ? "Pick at least one user." : "Pick at least one role, zone or sub-zone."}
                </span>
              )}
              {preview.status === "loading" && (
                <span className="flex items-center gap-2 text-neutral-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Counting users…
                </span>
              )}
              {preview.status === "error" && <span className="text-rose-600">{preview.error}</span>}
              {preview.status === "ok" && (
                <div className="space-y-1">
                  <div className={"flex items-center gap-2 font-semibold " + (preview.count ? "text-neutral-900" : "text-rose-600")}>
                    <Users className="h-4 w-4 shrink-0" />
                    {preview.count
                      ? <span>Will be sent to <span className="text-[#6D2C91]" data-testid="bc-preview-count">{preview.count}</span> user{preview.count === 1 ? "" : "s"}</span>
                      : <span>No users match this audience</span>}
                  </div>
                  {preview.count > 0 && <div className="text-[11px] text-neutral-500 break-words">{sampleText()}</div>}
                </div>
              )}
            </div>
          </div>
        </div>

        {sending && progress && (
          <div className="rounded-xl border border-[#6D2C91]/20 bg-[#6D2C91]/5 p-3 space-y-1.5" data-testid="bc-progress">
            <div className="flex items-center gap-2 text-xs text-neutral-700 min-w-0">
              <Loader2 className="h-4 w-4 animate-spin shrink-0 text-[#6D2C91]" />
              <span className="truncate">
                {progress.sending
                  ? "Sending the message…"
                  : `Uploading file ${progress.index + 1} of ${progress.count}: ${progress.name} (${progress.percent}%)`}
              </span>
            </div>
            {!progress.sending && (
              <div className="h-1.5 rounded-full bg-white overflow-hidden">
                <div className="h-full rounded-full tulsi-gradient transition-all" style={{ width: `${progress.percent}%` }} />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-100 pt-3">
          <Button type="button" variant="outline" onClick={reset} disabled={sending} data-testid="bc-clear">
            Clear form
          </Button>
          <Button type="button" className="tulsi-gradient text-white" onClick={openConfirm} disabled={sending}
                  data-testid="bc-send">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send message
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="bc-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send to {preview.count || 0} user{preview.count === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-neutral-600">
                <div className="flex items-center gap-2 flex-wrap">
                  <PriorityBadge priority={priority} />
                  <span className="font-semibold text-neutral-900 break-words">{title.trim()}</span>
                </div>
                <div>
                  They will see it as a popup the next time they open the app, and it stays until they tap
                  “OK, I have read it”.
                </div>
                <ul className="list-disc pl-5 text-xs space-y-0.5">
                  {files.length > 0 && <li>{files.length} attachment{files.length === 1 ? "" : "s"} ({fmtSize(files.reduce((n, f) => n + f.size, 0))})</li>}
                  <li>{expiresOn ? `Popup stops after ${fmtYmd(expiresOn)}` : "No expiry date"}</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bc-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} className="tulsi-gradient text-white" data-testid="bc-confirm-send">
              <Send className="h-4 w-4 mr-1.5" /> Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ---- report dialog ------------------------------------------------------------
function ReportDialog({ broadcast, onClose }) {
  const id = broadcast?.id || null;
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setDoc(broadcast); setStatus("all"); setPage(1); setRows(null);
    api.get(`/broadcasts/${encodeURIComponent(id)}`)
      .then((r) => { if (!cancelled) setDoc(r.data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); });
    return () => { cancelled = true; };
    // Only when a different message is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const s = ++seq.current;
    setLoading(true);
    api.get(`/broadcasts/${encodeURIComponent(id)}/recipients`, {
      params: { status, format: "json", page, page_size: REPORT_PAGE_SIZE }, timeout: 60000,
    })
      .then((r) => { if (s === seq.current) setRows(r.data); })
      .catch((e) => { if (s === seq.current) toast.error(formatApiError(e)); })
      .finally(() => { if (s === seq.current) setLoading(false); });
  }, [id, status, page]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await authedFetch(`/broadcasts/${encodeURIComponent(id)}/recipients?status=${status}&format=xlsx`);
      const slug = (doc?.title || "message").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").slice(0, 40) || "message";
      const label = { all: "all", read: "read", unread: "not-read" }[status];
      saveBlob(await res.blob(), filenameFrom(res, `broadcast-${slug}-${label}.xlsx`));
    } catch {
      toast.error("Could not download the Excel report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const readCount = rows?.read_count ?? doc?.read_count ?? 0;
  const unreadCount = rows?.unread_count ?? Math.max(0, (doc?.recipient_count || 0) - readCount);
  const tabCount = { all: readCount + unreadCount, read: readCount, unread: unreadCount };
  const total = rows?.total || 0;

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6 gap-3 grid-cols-1" data-testid="bc-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <BarChart3 className="h-4 w-4 text-[#6D2C91] shrink-0" /> Read report
          </DialogTitle>
        </DialogHeader>

        {doc && (
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2" data-testid="bc-report-message">
            <div className="flex items-center gap-1.5 flex-wrap">
              <PriorityBadge priority={doc.priority} />
              <StatusBadge b={doc} />
            </div>
            <div className="text-sm font-semibold text-neutral-900 break-words">{doc.title}</div>
            <div className="text-[11px] text-neutral-500 space-y-0.5">
              <div>
                Sent by {doc.created_by_name || "—"} · {fmtDateTime(doc.created_at)}
                {doc.expires_on ? ` · Popup until ${fmtYmd(doc.expires_on)}` : ""}
              </div>
              {doc.audience_summary && <div>To: {doc.audience_summary}</div>}
              {doc.is_active === false && (
                <div className="text-rose-600">
                  Withdrawn{doc.withdrawn_by_name ? ` by ${doc.withdrawn_by_name}` : ""}
                  {doc.withdrawn_at ? ` · ${fmtDateTime(doc.withdrawn_at)}` : ""}
                </div>
              )}
            </div>
            <div className="text-sm text-neutral-800 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">{doc.body}</div>
            {(doc.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {doc.attachments.map((a) => <AttachmentChip key={a.id} att={a} />)}
              </div>
            )}
          </div>
        )}

        <ReadBar read={readCount} total={readCount + unreadCount} />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {REPORT_TABS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => { setStatus(k); setPage(1); }}
                      data-testid={`bc-report-tab-${k}`}
                      className={"rounded-full border px-2.5 py-1 text-xs font-medium " +
                        (status === k ? "bg-[#6D2C91] border-[#6D2C91] text-white" : "bg-white border-neutral-200 text-neutral-700")}>
                {label}
                <span className={"ml-1 " + (status === k ? "text-white/80" : "text-neutral-400")}>{tabCount[k]}</span>
              </button>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={downloadExcel} disabled={downloading || !id}
                  data-testid="bc-report-xlsx">
            {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Download Excel
          </Button>
        </div>

        <div className="rounded-xl border border-neutral-200 overflow-x-auto">
          {!rows && loading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>
          ) : (
            <table className="w-full text-sm" data-testid="bc-report-table">
              <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
                <tr>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left px-2">Role</th>
                  <th className="text-left px-2">Zone</th>
                  <th className="text-left px-2">Phone</th>
                  <th className="text-left px-2">Read</th>
                </tr>
              </thead>
              <tbody className={loading ? "opacity-60" : ""}>
                {(rows?.items || []).map((r) => (
                  <tr key={r.user_id} className="border-t border-neutral-100">
                    <td className="py-1.5 px-3 font-medium text-neutral-900">{r.name || "—"}</td>
                    <td className="px-2 text-neutral-500 whitespace-nowrap">{r.role_code || "—"}</td>
                    <td className="px-2 text-neutral-500">{r.zone || "—"}</td>
                    <td className="px-2 text-neutral-500 whitespace-nowrap">{r.phone || "—"}</td>
                    <td className="px-2 whitespace-nowrap">
                      {r.read_at ? (
                        <span className="text-xs text-emerald-700">{fmtDateTime(r.read_at)}</span>
                      ) : (
                        <span className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          Not read
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && (rows?.items || []).length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-neutral-400">
                    {status === "read" ? "Nobody has read it yet." : status === "unread" ? "Everyone has read it." : "No recipients."}
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {total > REPORT_PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span>Showing {(page - 1) * REPORT_PAGE_SIZE + 1}–{(page - 1) * REPORT_PAGE_SIZE + (rows?.items || []).length} of {total}</span>
            <span className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page * REPORT_PAGE_SIZE >= total || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---- page ---------------------------------------------------------------------
// ---------------------------------------------------------------- different message for each user
// Upload an Excel/CSV with one row per person (USER + MESSAGE [+ TITLE]); each person gets
// only their own text. Everything else (priority, expiry, attachments) is shared.
function PersonalComposer({ onSent }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);       // {total, valid, errors, rows, error_rows}
  const [checking, setChecking] = useState(false);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("normal");
  const [expiresOn, setExpiresOn] = useState("");
  const [files, setFiles] = useState([]);
  const [skipBad, setSkipBad] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);
  const uploaded = useRef(new WeakMap());

  const downloadSample = async () => {
    try {
      const res = await authedFetch("/broadcasts/personal-template");
      saveBlob(await res.blob(), "personal-messages-sample.xlsx");
    } catch {
      toast.error("Could not download the sample file. Please try again.");
    }
  };

  const check = async (f) => {
    setFile(f); setPreview(null); setSkipBad(false);
    if (!f) return;
    setChecking(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/broadcasts/personal/preview", fd, { timeout: 60000 });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setChecking(false);
    }
  };

  const changeFiles = (next) => {
    for (const f of files) {
      const id = !next.includes(f) && uploaded.current.get(f);
      if (id) { cancelUpload(id); uploaded.current.delete(f); }
    }
    setFiles(next);
  };

  const reset = () => {
    setFile(null); setPreview(null); setTitle(""); setPriority("normal"); setExpiresOn("");
    setFiles([]); setSkipBad(false); setProgress(null);
    uploaded.current = new WeakMap();
    if (inputRef.current) inputRef.current.value = "";
  };

  const sendCount = preview ? (preview.errors > 0 ? preview.valid : preview.total) : 0;

  const openConfirm = () => {
    if (!file || !preview) { toast.error("Please choose the file with the messages."); return; }
    if (!title.trim()) { toast.error("Please enter a title (used for rows that have no TITLE of their own)."); return; }
    if (expiresOn && expiresOn < todayIST()) { toast.error("The expiry date can't be in the past."); return; }
    if (preview.valid === 0) { toast.error("There is no valid row in this file."); return; }
    if (preview.errors > 0 && !skipBad) { toast.error("Some rows have a problem. Fix the file, or tick “Send only the valid rows”."); return; }
    setConfirmOpen(true);
  };

  const doSend = async () => {
    setSending(true);
    let stage = "upload";
    const uploadIds = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        let id = uploaded.current.get(f);
        if (!id) {
          setProgress({ index: i, count: files.length, name: f.name, percent: 0 });
          try {
            id = await uploadInChunks(f, {
              onProgress: ({ percent }) => setProgress({ index: i, count: files.length, name: f.name, percent }),
            });
          } catch (e) {
            throw new Error(`“${f.name}” could not be uploaded. ${formatApiError(e)}`);
          }
          uploaded.current.set(f, id);
        }
        uploadIds.push(id);
      }
      stage = "send";
      setProgress({ sending: true });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("priority", priority);
      fd.append("expires_on", expiresOn || "");
      fd.append("skip_errors", skipBad ? "1" : "0");
      fd.append("upload_ids", uploadIds.join(","));
      const { data } = await api.post("/broadcasts/personal", fd, { timeout: 180000 });
      const n = data.recipient_count ?? sendCount;
      toast.success(`${n} personal message${n === 1 ? "" : "s"} sent.`);
      reset();
      onSent(data);
    } catch (e) {
      toast.error(formatApiError(e));
      if (stage === "send") {
        if (e?.response?.status === 422) {
          uploadIds.forEach((id) => cancelUpload(id));
          uploaded.current = new WeakMap();
        }
        onSent(null);
      }
      setProgress(null);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="bc-personal">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FileSpreadsheet className="h-4 w-4 text-[#6D2C91]" />
          <h2 className="text-sm font-bold text-neutral-900">A different message for each user</h2>
        </div>
        <p className="text-xs text-neutral-500">
          Upload an Excel or CSV file with one row per person: <b>USER</b> (email, phone or full name), <b>MESSAGE</b>, and an optional <b>TITLE</b>.
          Each person sees only their own message.
        </p>

        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">1. File with the messages</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={downloadSample} data-testid="bc-personal-sample">
                  <Download className="h-4 w-4 mr-1.5" /> Download sample file
                </Button>
                <input ref={inputRef} type="file" accept=".xlsx,.csv" disabled={sending}
                       onChange={(e) => check(e.target.files?.[0] || null)}
                       className="text-xs file:mr-2 file:rounded-md file:border file:border-neutral-200 file:bg-white file:px-3 file:py-1.5 file:text-xs"
                       data-testid="bc-personal-file" />
              </div>
              {checking && <div className="text-xs text-neutral-500 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking the file…</div>}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">2. Title <span className="text-neutral-400 font-normal">(used for rows without their own TITLE)</span></Label>
              <Input value={title} maxLength={TITLE_MAX} disabled={sending} onChange={(e) => setTitle(e.target.value)}
                     placeholder="e.g. Your October target" data-testid="bc-personal-title" />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Priority</Label>
                <div className="flex rounded-xl border border-neutral-200 bg-neutral-50 p-0.5">
                  <button type="button" disabled={sending} onClick={() => setPriority("normal")}
                          className={"flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold " + (priority === "normal" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500")}>
                    Normal
                  </button>
                  <button type="button" disabled={sending} onClick={() => setPriority("urgent")}
                          className={"flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center justify-center gap-1 " + (priority === "urgent" ? "bg-red-600 text-white shadow-sm" : "text-neutral-500")}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Urgent
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Expiry date <span className="text-neutral-400 font-normal">(optional)</span></Label>
                <Input type="date" value={expiresOn} min={todayIST()} disabled={sending}
                       onChange={(e) => setExpiresOn(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Attachments for everyone <span className="text-neutral-400 font-normal">(optional)</span></Label>
              <FilePicker files={files} onChange={changeFiles} disabled={sending} />
            </div>
          </div>

          <div className="space-y-3" data-testid="bc-personal-preview">
            {!preview ? (
              <div className="rounded-xl border border-dashed border-neutral-200 p-5 text-center text-xs text-neutral-400">
                Choose a file to see who will get which message.
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-neutral-200 p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span><b>{preview.total}</b> rows</span>
                    <span className="text-emerald-700"><b>{preview.valid}</b> ready</span>
                    {preview.errors > 0 && <span className="text-red-600"><b>{preview.errors}</b> with a problem</span>}
                  </div>
                  {preview.errors > 0 && (
                    <>
                      <ul className="mt-2 space-y-0.5 text-[11px] text-red-700 max-h-32 overflow-y-auto">
                        {preview.error_rows.slice(0, 20).map((r) => (
                          <li key={r.row}>Row {r.row}{r.user_input ? ` (${r.user_input})` : ""}: {r.error}</li>
                        ))}
                        {preview.errors > 20 && <li>…and {preview.errors - 20} more.</li>}
                      </ul>
                      <label className="mt-2 flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                        <input type="checkbox" checked={skipBad} onChange={(e) => setSkipBad(e.target.checked)} disabled={sending || preview.valid === 0} />
                        Send only the {preview.valid} valid rows (skip the {preview.errors} with a problem)
                      </label>
                    </>
                  )}
                </div>
                <div className="rounded-xl border border-neutral-200 overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-neutral-50 text-[10px] uppercase text-neutral-500 tracking-wider sticky top-0">
                        <tr><th className="text-left px-2 py-1.5">User</th><th className="text-left px-2">Message</th></tr>
                      </thead>
                      <tbody>
                        {preview.rows.filter((r) => !r.error).slice(0, 30).map((r) => (
                          <tr key={r.row} className="border-t border-neutral-100 align-top">
                            <td className="px-2 py-1.5 whitespace-nowrap">{r.user_name}<div className="text-[10px] text-neutral-400">{r.role_code}</div></td>
                            <td className="px-2 py-1.5">{r.title ? <b>{r.title} — </b> : null}{r.message.length > 140 ? r.message.slice(0, 140) + "…" : r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {preview.valid > 30 && <div className="px-2 py-1.5 text-[11px] text-neutral-400 border-t">Showing the first 30 of {preview.valid}.</div>}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-neutral-100">
          <div className="text-xs text-neutral-500 min-h-[1.25rem]">
            {progress?.sending ? "Sending…"
              : progress ? `Uploading ${progress.name} (${progress.index + 1}/${progress.count}) ${progress.percent ?? 0}%`
              : preview && sendCount > 0 ? <>Will be sent to <b className="text-neutral-900">{sendCount}</b> user{sendCount === 1 ? "" : "s"}, each with their own message.</> : ""}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={reset} disabled={sending} data-testid="bc-personal-clear">Clear form</Button>
            <Button type="button" onClick={openConfirm} disabled={sending || checking} className="tulsi-gradient text-white font-semibold" data-testid="bc-personal-send">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send messages
            </Button>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send {sendCount} personal message{sendCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Each person gets only their own message{files.length ? `, with ${files.length} attachment${files.length === 1 ? "" : "s"} for everyone` : ""}. This can’t be edited afterwards, only withdrawn.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} className="tulsi-gradient text-white">Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function Broadcasts() {
  const [roles, setRoles] = useState(ROLE_FALLBACK);
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [reportOf, setReportOf] = useState(null);
  const [withdrawOf, setWithdrawOf] = useState(null);
  const [composeTab, setComposeTab] = useState("same");
  const [withdrawing, setWithdrawing] = useState(false);
  const [dlId, setDlId] = useState(null);       // message whose Excel is being downloaded
  const [dlAll, setDlAll] = useState(false);
  const listSeq = useRef(0);

  // Every active user, zone, sub-zone and role in one sender-only call. /users
  // and /zones are limited to what the caller can normally see, which would
  // cut the Sales Co-ordinator's pickers down to their own team.
  useEffect(() => {
    api.get("/broadcasts/audience-options", { timeout: 30000 })
      .then(({ data }) => {
        if (data?.roles?.length) setRoles(data.roles);
        setZones(data?.zones || []);
        setSubZones(data?.sub_zones || []);
        setUsers(data?.users || []);
      })
      .catch((e) => toast.error(`Could not load the user list. ${formatApiError(e)}`))
      .finally(() => setUsersLoading(false));
  }, []);

  const roleOptions = useMemo(() => roles.map((r) => ({
    id: r.code, label: r.name || r.code, hint: r.code, search: `${r.name || ""} ${r.code}`,
  })), [roles]);
  const zoneOptions = useMemo(() => zones.map((z) => ({
    id: z.id, label: z.name || z.code, hint: z.code, search: `${z.name || ""} ${z.code || ""}`,
  })), [zones]);
  const subZoneOptions = useMemo(() => subZones.map((s) => ({
    id: s.id, label: s.name || s.code, hint: s.code, search: `${s.name || ""} ${s.code || ""}`,
  })), [subZones]);
  const userOptions = useMemo(() => users
    .filter((u) => u.is_active !== false)
    .map((u) => ({
      id: u.id, label: u.name || u.phone || u.email || "—", hint: u.role_code,
      search: [u.name, u.role_code, u.phone, u.email].filter(Boolean).join(" "),
    }))
    .sort((a, b) => a.label.localeCompare(b.label)), [users]);

  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const loadList = useCallback(async ({ silent = false } = {}) => {
    const s = ++listSeq.current;
    if (!silent) setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (q) params.q = q;
      const { data } = await api.get("/broadcasts", { params });
      if (s === listSeq.current) setList({ items: data.items || [], total: data.total || 0 });
    } catch (e) {
      if (s === listSeq.current) toast.error(formatApiError(e));
    } finally {
      if (s === listSeq.current) setLoading(false);
    }
  }, [page, q]);

  useEffect(() => { loadList(); }, [loadList]);

  const onSent = () => {
    if (page !== 1) setPage(1);
    else loadList({ silent: true });
  };

  const doWithdraw = async () => {
    if (!withdrawOf) return;
    setWithdrawing(true);
    try {
      await api.post(`/broadcasts/${encodeURIComponent(withdrawOf.id)}/withdraw`);
      toast.success("Message withdrawn. The popup will no longer show.");
      setWithdrawOf(null);
      loadList({ silent: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWithdrawing(false);
    }
  };

  // ONE Excel with every message's read report (respects the search box).
  const downloadAll = async () => {
    setDlAll(true);
    try {
      const res = await authedFetch(`/broadcasts/report-all${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      saveBlob(await res.blob(), filenameFrom(res, "broadcast-messages-all-reports.xlsx"));
    } catch {
      toast.error("Could not download the report. Please try again.");
    } finally {
      setDlAll(false);
    }
  };

  // One-click Excel of who read / who has not read, straight from the list.
  const downloadReport = async (b) => {
    setDlId(b.id);
    try {
      const res = await authedFetch(`/broadcasts/${encodeURIComponent(b.id)}/recipients?status=all&format=xlsx`);
      const slug = (b.title || "message").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").slice(0, 40) || "message";
      saveBlob(await res.blob(), filenameFrom(res, `broadcast-${slug}-report.xlsx`));
    } catch {
      toast.error("Could not download the Excel report. Please try again.");
    } finally {
      setDlId(null);
    }
  };

  const items = list.items;
  const actions = (b) => (
    <div className="flex items-center gap-1.5 justify-end">
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setReportOf(b)}
              data-testid={`bc-report-${b.id}`}>
        <Eye className="h-3.5 w-3.5 mr-1" /> Report
      </Button>
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => downloadReport(b)}
              disabled={dlId === b.id} data-testid={`bc-xlsx-${b.id}`}>
        {dlId === b.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />} Excel
      </Button>
      {b.is_active !== false && (
        <Button type="button" variant="outline" size="sm" className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={() => setWithdrawOf(b)} data-testid={`bc-withdraw-${b.id}`}>
          <Ban className="h-3.5 w-3.5 mr-1" /> Withdraw
        </Button>
      )}
    </div>
  );

  return (
    <div className="p-4 space-y-4" data-testid="broadcasts-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Settings" }, { label: "Broadcast Messages" }]}
        title="Broadcast Messages"
        subtitle="Send one message to many people at once. They get a notification and a popup that stays until they tap “OK, I have read it”."
        testid="broadcasts-header"
      />

      <div className="flex rounded-xl border border-neutral-200 bg-neutral-50 p-0.5 w-full sm:w-fit" role="tablist" data-testid="bc-compose-tabs">
        {[["same", "Same message to many"], ["personal", "Different message for each user (file)"]].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={composeTab === k} onClick={() => setComposeTab(k)}
                  data-testid={`bc-tab-${k}`}
                  className={"flex-1 sm:flex-none rounded-lg px-4 py-2 text-xs font-semibold transition-colors " +
                    (composeTab === k ? "bg-white text-[#6D2C91] shadow-sm" : "text-neutral-500 hover:text-neutral-800")}>
            {label}
          </button>
        ))}
      </div>

      {composeTab === "same" ? (
        <Composer
          roleOptions={roleOptions} zoneOptions={zoneOptions} subZoneOptions={subZoneOptions}
          userOptions={userOptions} usersLoading={usersLoading} onSent={onSent}
        />
      ) : (
        <PersonalComposer onSent={onSent} />
      )}

      <div className="space-y-3" data-testid="bc-sent">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <h2 className="text-sm font-bold text-neutral-900 flex-1">
            Sent messages <span className="font-normal text-neutral-400">({list.total})</span>
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input value={qInput} onChange={(e) => setQInput(e.target.value)} className="pl-8 h-9"
                     placeholder="Search title or message…" data-testid="bc-search" />
            </div>
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={downloadAll}
                    disabled={dlAll || !items.length} data-testid="bc-download-all">
              {dlAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
              Download all (Excel)
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => loadList()}
                    disabled={loading} aria-label="Refresh" data-testid="bc-refresh">
              <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
            </Button>
          </div>
        </div>

        {loading && !items.length ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>
        ) : !items.length ? (
          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="py-12 text-center text-sm text-neutral-500" data-testid="bc-empty">
              {q ? "No messages match your search." : "No messages sent yet. Write one above to get started."}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop: table */}
            <Card className="hidden md:block border-neutral-200 rounded-2xl">
              <CardContent className="p-0 overflow-x-auto">
                <table className={"w-full text-sm " + (loading ? "opacity-60" : "")} data-testid="bc-table">
                  <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
                    <tr>
                      <th className="text-left py-2.5 px-3">Message</th>
                      <th className="text-left px-2">Priority</th>
                      <th className="text-left px-2">Sent</th>
                      <th className="text-left px-2">Read</th>
                      <th className="text-left px-2">Status</th>
                      <th className="px-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((b) => (
                      <tr key={b.id} className="border-t border-neutral-100 align-top" data-testid={`bc-row-${b.id}`}>
                        <td className="py-2 px-3">
                          {/* A div, not the td, carries the width cap — tables ignore max-width on cells. */}
                          <div className="min-w-[180px] max-w-[320px]">
                            <button type="button" onClick={() => setReportOf(b)}
                                    className="block max-w-full truncate text-left font-medium text-neutral-900 hover:text-[#6D2C91]">
                              {b.title}
                            </button>
                            <div className="text-[11px] text-neutral-500 truncate">{b.body}</div>
                            {b.audience_summary && (
                              <div className="text-[11px] text-neutral-400 truncate" title={b.audience_summary}>To: {b.audience_summary}</div>
                            )}
                            {(b.attachments || []).length > 0 && (
                              <div className="text-[11px] text-neutral-400 flex items-center gap-0.5 mt-0.5">
                                <Paperclip className="h-3 w-3" /> {b.attachments.length}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2"><PriorityBadge priority={b.priority} /></td>
                        <td className="px-2 py-2 text-xs text-neutral-600 whitespace-nowrap">
                          <div>{fmtDateTime(b.created_at)}</div>
                          <div className="text-neutral-400">by {b.created_by_name || "—"}</div>
                          {b.expires_on && (
                            <div className="text-neutral-400 flex items-center gap-0.5">
                              <CalendarClock className="h-3 w-3" /> until {fmtYmd(b.expires_on)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2"><ReadBar read={b.read_count} total={b.recipient_count} /></td>
                        <td className="px-2 py-2"><StatusBadge b={b} /></td>
                        <td className="px-3 py-2">{actions(b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Mobile: cards */}
            <div className={"md:hidden space-y-2 " + (loading ? "opacity-60" : "")}>
              {items.map((b) => (
                <Card key={b.id} className="border-neutral-200 rounded-2xl" data-testid={`bc-card-${b.id}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <button type="button" onClick={() => setReportOf(b)}
                              className="min-w-0 text-left text-sm font-semibold text-neutral-900 break-words">
                        {b.title}
                      </button>
                      <div className="flex items-center gap-1 shrink-0">
                        <PriorityBadge priority={b.priority} />
                        <StatusBadge b={b} />
                      </div>
                    </div>
                    <div className="text-[11px] text-neutral-500">
                      {fmtDateTime(b.created_at)} · by {b.created_by_name || "—"}
                      {b.expires_on ? ` · until ${fmtYmd(b.expires_on)}` : ""}
                      {(b.attachments || []).length > 0 ? ` · ${b.attachments.length} file${b.attachments.length === 1 ? "" : "s"}` : ""}
                    </div>
                    {b.audience_summary && <div className="text-[11px] text-neutral-400 break-words">To: {b.audience_summary}</div>}
                    <ReadBar read={b.read_count} total={b.recipient_count} />
                    {actions(b)}
                  </CardContent>
                </Card>
              ))}
            </div>

            {list.total > PAGE_SIZE && (
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>Showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + items.length} of {list.total}</span>
                <span className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} data-testid="bc-prev">Prev</Button>
                  <Button variant="outline" size="sm" disabled={page * PAGE_SIZE >= list.total || loading} onClick={() => setPage((p) => p + 1)} data-testid="bc-next">Next</Button>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <ReportDialog broadcast={reportOf} onClose={() => setReportOf(null)} />

      <AlertDialog open={!!withdrawOf} onOpenChange={(v) => { if (!v && !withdrawing) setWithdrawOf(null); }}>
        <AlertDialogContent data-testid="bc-withdraw-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this message?</AlertDialogTitle>
            <AlertDialogDescription>
              “{withdrawOf?.title}” will stop popping up for people who have not read it yet. It stays in their
              Messages list, marked Withdrawn. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={withdrawing}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doWithdraw(); }}
              disabled={withdrawing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bc-withdraw-confirm"
            >
              {withdrawing && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
